import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, useTheme } from "@mui/material";
import Button from "components/basic/button";
import Typography from "components/basic/typography";
import { useSnack } from "hooks/useSnack";
import AvailabilityModal from "pages/dashboard/pages/calendar/setAvailability/AvailabilityModal";
import { useVendorAvailability } from "pages/dashboard/pages/calendar/setAvailability/availabilityApi";
import { AvailabilityData } from "pages/dashboard/pages/calendar/setAvailability/types";

interface AvailabilityStepProps {
  onSaved: () => void;
}

/** The days and hours the vendor takes bookings, which unlocks the calendar. */
const AvailabilityStep: React.FC<AvailabilityStepProps> = ({ onSaved }) => {
  const theme = useTheme();
  const { t } = useTranslation("onboarding");
  const showSnack = useSnack();
  const { availability, save } = useVendorAvailability(true);
  const [open, setOpen] = useState(false);

  const handleSave = async (next: AvailabilityData) => {
    const result = await save(next);
    if (!result.ok) {
      showSnack(result.error ?? t("availability.save_failed"), "error");
      return;
    }
    setOpen(false);
    onSaved();
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Typography variant="body1">{t("availability.intro")}</Typography>
      <Button
        variant="contained"
        color="warning"
        style={{ color: theme.palette.common.white, alignSelf: "flex-start" }}
        onClick={() => setOpen(true)}
        disabled={availability === undefined}
      >
        {t(availability ? "availability.edit" : "availability.set")}
      </Button>

      {open && (
        <AvailabilityModal
          initial={availability ?? null}
          onClose={() => setOpen(false)}
          onSave={handleSave}
        />
      )}
    </Box>
  );
};

export default AvailabilityStep;
